const hasDisplayValue = (value) =>
    value !== null &&
    value !== undefined &&
    String(value).trim() !== "";

const normalizeText = (value = "") => {
    if (typeof value === "string") {
        return String(value || "").trim();
    }

    if (value && typeof value === "object") {
        return String(value.status || value.text || value.label || "").trim();
    }

    return "";
};

const parseScoreValue = (value = "") => {
    const normalizedValue = String(value || "").trim();
    if (!normalizedValue) return "";

    const scoreMatch = normalizedValue.match(/^(.*?)(?:\s*\(([\d.]+)\))?$/);
    return String(scoreMatch?.[1] || normalizedValue).trim();
};

const getFirstAvailableValue = (...values) => {
    const matchedValue = values.find((value) => hasDisplayValue(value));
    return matchedValue === undefined ? "" : normalizeText(matchedValue);
};

const extractOversValue = (value = "") => {
    const normalizedValue = normalizeText(value);

    if (!normalizedValue) {
        return "";
    }

    const oversWithLabelMatch = normalizedValue.match(
        /(\d+(?:\.\d+)?)(?:\s*\/\s*\d+(?:\.\d+)?)?\s*ov/i,
    );

    if (oversWithLabelMatch?.[1]) {
        return oversWithLabelMatch[1];
    }

    const plainOversMatch = normalizedValue.match(
        /^(\d+(?:\.\d+)?)(?:\s*\/\s*\d+(?:\.\d+)?)?$/,
    );

    if (plainOversMatch?.[1]) {
        return plainOversMatch[1];
    }

    return "";
};

const formatScoreLine = (runs = "", meta = "") => {
    const normalizedRuns = parseScoreValue(runs);
    const normalizedMeta = extractOversValue(meta);

    if (
        !normalizedRuns ||
        normalizedRuns.toLowerCase().includes("ov") ||
        normalizedRuns.toLowerCase().includes("t:")
    ) {
        return "-";
    }

    return `${normalizedRuns}${normalizedMeta ? ` (${normalizedMeta})` : ""}`;
};

export const getMatchStatus = (match = {}) => {
    if (!match || typeof match !== "object") {
        return {
            text: "UNKNOWN",
            bg: "bg-secondary text-white",
            isCancelled: false,
            info: "",
        };
    }

    const apiStatus = normalizeText(
        match.apiStatus || match.event_status || match.api_status,
    ).toLowerCase();
    const statusInfo = normalizeText(
        match.status_info || match.event_status_info || match.statusInfo || match.statusText,
    );
    const statusInfoLower = statusInfo.toLowerCase();
    const mainStatus = normalizeText(match.status).toLowerCase();

    if (
        apiStatus === "cancelled" ||
        apiStatus === "abandoned" ||
        statusInfoLower.includes("no result") ||
        statusInfoLower.includes("abandoned")
    ) {
        return {
            text: "CANCELLED",
            bg: "bg-secondary text-white",
            isCancelled: true,
            info: statusInfo || "Match Abandoned",
        };
    }

    if (
        ["completed", "finished", "result"].some((value) =>
            `${apiStatus} ${mainStatus}`.includes(value),
        )
    ) {
        return {
            text: "COMPLETED",
            bg: "bg-success text-white",
            isCancelled: false,
            info: statusInfo || "Match Ended",
        };
    }

    if (mainStatus === "live" || match.isLive === true) {
        return {
            text: "LIVE",
            bg: "bg-danger text-white",
            isCancelled: false,
            info: normalizeText(match.toss || match.event_toss) || "Live Match",
        };
    }

    return {
        text: "UPCOMING",
        bg: "bg-warning text-dark",
        isCancelled: false,
        info: statusInfo || "Match not started",
    };
};

export const getTeamScore = (match = {}, teamType = "home") => {
    if (!match || typeof match !== "object") {
        return "-";
    }

    const matchStatus = getMatchStatus(match);
    if (matchStatus.isCancelled) {
        return "-";
    }

    const isHomeTeam = teamType === "home";
    const finalResult = getFirstAvailableValue(
        isHomeTeam ? match.event_home_final_result : match.event_away_final_result,
    );
    const serviceInfo = getFirstAvailableValue(
        isHomeTeam ? match.event_service_home : match.event_service_away,
    );

    if (finalResult) {
        return formatScoreLine(finalResult, serviceInfo);
    }

    const runs = getFirstAvailableValue(
        isHomeTeam ? match?.score?.home?.runs : match?.score?.away?.runs,
    );
    const overs = getFirstAvailableValue(match?.score?.overs);

    return formatScoreLine(runs, overs);
};
